import {useEffect, useMemo, useState} from "react";
import _ from "lodash";
import {useAtom, useAtomValue} from "jotai";
import {Field} from "../types/domain/field";
import {getAllFields} from "../services/fieldService";
import {activeCompanyResultsLoadable} from "../store/activeCompanyResultsStore";
import {
    isCropGrowthAlert,
    isSoilMoistureAlert,
    normalizeCropGrowth,
    normalizeSoilMoisture,
} from "../utils/insightsUtils";
import {FieldResultStatistics} from "../types/domain/fieldResult";
import {activeMergedFarmIds} from "../store/activeMergedFarmIds";
import {useQuery} from "@tanstack/react-query";
import {useActiveCompanyFields} from "./activeCompanyFieldsHooks";

export interface FieldStatus extends FieldResultStatistics {
    isSoilMoistureAlert: boolean;
    isCropGrowthAlert: boolean;
}

interface UseFieldsParams {
    companyId?: string;
    orderId?: string;
    farmId?: string;
    cropId?: string;
    onlyActiveOrders?: boolean;
    name?: string;
    attributes?: string[];
}

export const useFields = (params: UseFieldsParams) => {
    return useQuery({
        queryKey: ["fields", params],
        queryFn: () => getAllFields({...params, companyId: params.companyId!}),
        enabled: !!params.companyId,
        staleTime: 1000 * 60 * 5,
    });
};

export const useMultiFarmFields = ({companyId}: {companyId: string}) => {
    const [activeMergedFarmIdsAtom] = useAtom(activeMergedFarmIds);
    const [items, setItems] = useState<Field[]>([]);

    useEffect(() => {
        if (!companyId || activeMergedFarmIdsAtom.length === 0) return;
        void Promise.all(activeMergedFarmIdsAtom.map((farmId) => getAllFields({companyId, farmId}))).then((res) => {
            setItems(res.flat());
        });
    }, [companyId, activeMergedFarmIdsAtom]);

    return items;
};

export const useFieldStatusById = () => {
    const activeCompanyResultsState = useAtomValue(activeCompanyResultsLoadable);
    const fieldsState = useActiveCompanyFields();
    const fields = useMemo(() => (fieldsState.isSuccess ? fieldsState.data.fields : []), [fieldsState]);

    const moistureAlertThreshold = (id: string) => {
        const field = fields.find((item) => item.id === id);
        return field?.irrigationCriteria ?? 0.1;
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    return useMemo(() => {
        if (activeCompanyResultsState.state === "hasData") {
            return _.mapValues(
                activeCompanyResultsState.data,
                (results: FieldResultStatistics, key) =>
                    ({
                        soilMoisture: normalizeSoilMoisture(results.soilMoisture),
                        cropGrowth: normalizeCropGrowth(results.cropGrowth),
                        timeUntilNextIrrigation: results.timeUntilNextIrrigation,
                        isSoilMoistureAlert: isSoilMoistureAlert(
                            results.soilMoisture,
                            moistureAlertThreshold(key),
                            results.vegetationCover,
                        ),
                        isCropGrowthAlert: isCropGrowthAlert(results.cropGrowth, results.vegetationCover),
                    }) as FieldStatus,
            );
        } else {
            return {};
        }
    }, [activeCompanyResultsState]);
};
